// PROTOTYPE
import type { JSX } from 'react';
import type {
  DOMConversionMap,
  DOMConversionOutput,
  DOMExportOutput,
  LexicalEditor,
  LexicalNode,
  NodeKey,
  SerializedLexicalNode,
  Spread,
} from 'lexical';
import { $applyNodeReplacement, DecoratorNode } from 'lexical';
import {
  getVideoEmbedProvider,
  isAllowedVideoEmbedSrc,
  isPlayableVideoSrc,
  parseVideoCarrierHref,
  toVideoCarrierHref,
  type VideoEmbedProvider,
} from '../videoEmbed';
import { VideoComponent } from './VideoComponent';

export type VideoSource = 'embed' | 'upload';

export interface VideoPayload {
  height?: number;
  provider?: VideoEmbedProvider | null;
  source: VideoSource;
  src: string;
  title?: string;
  width?: number;
}

export type SerializedVideoNode = Spread<
  {
    height: number;
    provider: VideoEmbedProvider | null;
    source: VideoSource;
    src: string;
    title: string;
    type: 'video';
    version: 1;
    width: number;
  },
  SerializedLexicalNode
>;

export const DEFAULT_VIDEO_WIDTH = 640;
export const DEFAULT_VIDEO_HEIGHT = 360;

function parseDimension(value: string | null): number | null {
  if (!value) {
    return null;
  }

  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : null;
}

// The import-boundary gate: an iframe whose src fails the allowlist is never adopted into a node,
// so it can never be re-emitted by exportDOM().
function convertVideoIframeElement(
  domNode: globalThis.HTMLElement
): DOMConversionOutput | null {
  const src = domNode.getAttribute('src');

  if (!isAllowedVideoEmbedSrc(src)) {
    return null;
  }

  return {
    node: $createVideoNode({
      height:
        parseDimension(domNode.getAttribute('height')) ?? DEFAULT_VIDEO_HEIGHT,
      provider: getVideoEmbedProvider(src ?? ''),
      source: 'embed',
      src: src ?? '',
      title: domNode.getAttribute('title') ?? '',
      width: parseDimension(domNode.getAttribute('width')) ?? DEFAULT_VIDEO_WIDTH,
    }),
  };
}

// Anchor carrier import: the marker+allowlist check already ran in parseVideoCarrierHref via the
// importDOM matcher below, so a non-carrier <a> never reaches this conversion.
function convertVideoCarrierAnchorElement(
  domNode: globalThis.HTMLElement
): DOMConversionOutput | null {
  const src = parseVideoCarrierHref(domNode.getAttribute('href'));

  if (!src) {
    return null;
  }

  return {
    node: $createVideoNode({
      provider: getVideoEmbedProvider(src),
      source: 'embed',
      src,
      title: domNode.textContent?.trim() ?? '',
    }),
  };
}

function convertVideoElement(
  domNode: globalThis.HTMLElement
): DOMConversionOutput | null {
  if (!(domNode instanceof globalThis.HTMLVideoElement)) {
    return null;
  }

  const src =
    domNode.getAttribute('src') ??
    domNode.querySelector('source')?.getAttribute('src') ??
    '';

  // Before this node existed a <video> was dropped on import, and the editable
  // path never runs DOMPurify — so without a scheme check this would newly let
  // stored markup fetch from an arbitrary origin.
  if (!src || !isPlayableVideoSrc(src)) {
    return null;
  }

  return {
    node: $createVideoNode({
      height:
        parseDimension(domNode.getAttribute('height')) ?? DEFAULT_VIDEO_HEIGHT,
      provider: null,
      source: 'upload',
      src,
      width: parseDimension(domNode.getAttribute('width')) ?? DEFAULT_VIDEO_WIDTH,
    }),
  };
}

export class VideoNode extends DecoratorNode<JSX.Element> {
  __height: number;
  __provider: VideoEmbedProvider | null;
  __source: VideoSource;
  __src: string;
  __title: string;
  __width: number;

  static getType(): string {
    return 'video';
  }

  static clone(node: VideoNode): VideoNode {
    return new VideoNode(
      node.__src,
      node.__source,
      node.__provider,
      node.__width,
      node.__height,
      node.__title,
      node.__key
    );
  }

  // isInline: false (below) is the deliberate difference from ImageNode — it makes the video a
  // top-level sibling of the paragraphs, so multiple videos can sit between arbitrary paragraphs.
  static importDOM(): DOMConversionMap | null {
    return {
      // Priority 2 beats LinkNode's priority 1 for a carrier anchor; returning null here for an
      // ordinary <a> lets it fall through to LinkNode as usual.
      a: (domNode) => {
        if (!parseVideoCarrierHref(domNode.getAttribute('href'))) {
          return null;
        }

        return {
          conversion: convertVideoCarrierAnchorElement,
          priority: 2,
        };
      },
      iframe: () => ({
        conversion: convertVideoIframeElement,
        priority: 0,
      }),
      video: () => ({
        conversion: convertVideoElement,
        priority: 0,
      }),
    };
  }

  static importJSON(serializedNode: SerializedVideoNode): VideoNode {
    return $createVideoNode(serializedNode).updateFromJSON(serializedNode);
  }

  constructor(
    src: string,
    source: VideoSource,
    provider: VideoEmbedProvider | null = null,
    width: number = DEFAULT_VIDEO_WIDTH,
    height: number = DEFAULT_VIDEO_HEIGHT,
    title = '',
    key?: NodeKey
  ) {
    super(key);
    this.__src = src;
    this.__source = source;
    this.__provider = provider;
    this.__width = width;
    this.__height = height;
    this.__title = title;
  }

  exportDOM(): DOMExportOutput {
    if (this.__source === 'embed') {
      // The Java sanitizer drops <iframe> but keeps <a href>, so an embed persists as a carrier
      // anchor (upgraded back to an iframe by sanitizeEditorHtml) instead of the live iframe.
      const anchorElement = globalThis.document.createElement('a');
      anchorElement.setAttribute('href', toVideoCarrierHref(this.__src));
      anchorElement.setAttribute('target', '_blank');
      anchorElement.setAttribute('rel', 'noopener noreferrer');
      anchorElement.textContent = this.__src;

      return { element: anchorElement };
    }

    const videoElement = globalThis.document.createElement('video');
    videoElement.setAttribute('src', this.__src);
    videoElement.setAttribute('controls', 'true');
    videoElement.setAttribute('width', String(this.__width));
    videoElement.setAttribute('height', String(this.__height));

    return { element: videoElement };
  }

  exportJSON(): SerializedVideoNode {
    return {
      height: this.__height,
      provider: this.__provider,
      source: this.__source,
      src: this.getSrc(),
      title: this.__title,
      ...super.exportJSON(),
      type: 'video',
      version: 1,
      width: this.__width,
    };
  }

  updateFromJSON(serializedNode: SerializedVideoNode): this {
    const writable = this.getWritable();
    writable.__height = serializedNode.height;
    writable.__provider = serializedNode.provider;
    writable.__source = serializedNode.source;
    writable.__src = serializedNode.src;
    writable.__title = serializedNode.title;
    writable.__width = serializedNode.width;
    return writable;
  }

  getSrc(): string {
    return this.getLatest().__src;
  }

  getSource(): VideoSource {
    return this.getLatest().__source;
  }

  getProvider(): VideoEmbedProvider | null {
    return this.getLatest().__provider;
  }

  createDOM(): globalThis.HTMLElement {
    const element = globalThis.document.createElement('div');
    element.className = 'seaters-editor-video';
    return element;
  }

  updateDOM(): false {
    return false;
  }

  isInline(): false {
    return false;
  }

  decorate(editor: LexicalEditor): JSX.Element {
    return (
      <VideoComponent
        editor={editor}
        height={this.__height}
        nodeKey={this.getKey()}
        source={this.__source}
        src={this.__src}
        title={this.__title}
        width={this.__width}
      />
    );
  }
}

export function $createVideoNode(payload: VideoPayload): VideoNode {
  return $applyNodeReplacement(
    new VideoNode(
      payload.src,
      payload.source,
      payload.provider ?? null,
      payload.width ?? DEFAULT_VIDEO_WIDTH,
      payload.height ?? DEFAULT_VIDEO_HEIGHT,
      payload.title ?? ''
    )
  );
}

export function $isVideoNode(
  node: LexicalNode | null | undefined
): node is VideoNode {
  return node instanceof VideoNode;
}
